import React from "react"
import { STATUS_COLORS, TYPOGRAPHY } from "../constants/theme"

/**
 * แปลง snake_case เป็น Title Case
 * @param {string} str - สตริงที่ต้องการแปลง
 * @returns {string} - สตริงที่แปลงแล้ว
 */
export const snakeToTitleCase = (str) => {
	if (!str) return ""

	return str
		.split("_")
		.map((word) => {
			// ถ้าเป็นคำสั้น ๆ (<=3 ตัวอักษร) ถือว่าเป็นตัวย่อ เช่น bbl -> BBL
			if (word.length <= 3) {
				return word.toUpperCase()
			}
			// คำทั่วไป -> ตัวแรกเป็นพิมพ์ใหญ่ ที่เหลือเป็นพิมพ์เล็ก
			return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
		})
		.join(" ")
}

/**
 * รับ style สำหรับสถานะต่างๆ
 * @param {string} status - สถานะ
 * @returns {Object} - style object
 */
export const getStatusStyle = (status) => {
	const config = STATUS_COLORS[status] || STATUS_COLORS.inactive

	return {
		color: config.color,
		backgroundColor: config.backgroundColor,
		fontWeight: 600,
		textTransform: "capitalize",
		padding: "4px 8px",
		borderRadius: "4px",
		fontSize: TYPOGRAPHY.fontSize.xs,
		display: "inline-block",
	}
}

/**
 * รับ style สำหรับสถานะแบบข้อความธรรมดา
 * @param {string} status - สถานะ
 * @returns {Object} - style object
 */
export const getStatusTextStyle = (status) => {
	const config = STATUS_COLORS[status] || STATUS_COLORS.inactive

	return {
		color: config.color,
		fontWeight: 600,
		textTransform: "capitalize",
		fontSize: TYPOGRAPHY.fontSize.sm,
	}
}

/**
 * รับ badge style สำหรับสถานะต่างๆ
 * @param {string} status - สถานะ
 * @returns {Object} - badge style object
 */
export const getStatusBadgeStyle = (status) => {
	const config = STATUS_COLORS[status] || STATUS_COLORS.inactive

	return {
		display: "inline-flex",
		alignItems: "center",
		background: config.backgroundColor,
		padding: "4px 10px",
		borderRadius: 20,
		fontWeight: 600,
		color: config.color,
		fontSize: TYPOGRAPHY.fontSize.sm,
		border: `1px solid ${config.borderColor}`,
	}
}

/**
 * คำนวณสถานะของ plan
 * @param {Object} plan - ข้อมูล plan
 * @returns {string} - สถานะ
 */
export const calculatePlanStatus = (plan) => {
	if (!plan.startAt || !plan.expiredAt) return "unknown"

	const now = new Date()
	const start = new Date(plan.startAt)
	const expired = new Date(plan.expiredAt)

	if (start <= now && expired >= now) {
		return "active"
	} else if (start > now && expired > now) {
		return "waiting"
	} else {
		return "expired"
	}
}

/**
 * สร้าง pagination info
 * @param {number} currentPage - หน้าปัจจุบัน
 * @param {number} perPage - จำนวนรายการต่อหน้า
 * @param {number} totalCount - จำนวนรายการทั้งหมด
 * @returns {Object} - pagination info
 */
export const getPaginationInfo = (currentPage, perPage, totalCount) => {
	const indexOfFirstItem = (currentPage - 1) * perPage + 1
	const indexOfLastItem = Math.min(currentPage * perPage, totalCount)
	const totalPages = Math.ceil(totalCount / perPage)

	return {
		indexOfFirstItem: totalCount > 0 ? indexOfFirstItem : 0,
		indexOfLastItem,
		totalPages,
		showingText: `แสดง ${totalCount > 0 ? indexOfFirstItem : 0}-${indexOfLastItem} จากทั้งหมด ${totalCount} รายการ`,
	}
}

/**
 * สร้าง array ของหน้าที่จะแสดงใน pagination
 * @param {number} currentPage - หน้าปัจจุบัน
 * @param {number} totalPages - จำนวนหน้าทั้งหมด
 * @returns {Array} - array ของ page items
 */
export const getPaginationPages = (currentPage, totalPages) => {
	const pages = []

	for (let i = 1; i <= totalPages; i++) {
		const showPage = i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1

		if (!showPage && i === 2 && currentPage > 4) {
			pages.push({ type: "ellipsis", key: "ellipsis1" })
			continue
		}

		if (!showPage && i === totalPages - 1 && currentPage < totalPages - 3) {
			pages.push({ type: "ellipsis", key: "ellipsis2" })
			continue
		}

		if (showPage) {
			pages.push({ type: "page", page: i, key: i })
		}
	}

	return pages
}

/**
 * format เงินเป็นรูปแบบไทย
 * @param {number} amount - จำนวนเงิน
 * @returns {string} - เงินที่ format แล้ว
 */
export const formatCurrency = (amount) => {
	if (amount === null || amount === undefined) return "-"
	return `${amount.toLocaleString()} ฿`
}

/**
 * ตรวจสอบว่าเป็น mobile หรือไม่
 * @returns {boolean}
 */
export const isMobile = () => {
	return window.innerWidth <= 600
}

/**
 * ตรวจสอบว่าเป็น tablet หรือไม่
 * @returns {boolean}
 */
export const isTablet = () => {
	return window.innerWidth <= 900 && window.innerWidth > 600
}

/**
 * ฟังก์ชันสำหรับ highlight คำค้นหาใน text
 * @param {string} text - ข้อความต้นฉบับ
 * @param {string} searchQuery - คำค้นหา
 * @returns {JSX.Element|string} - ข้อความที่ถูก highlight
 */
export const highlightText = (text, searchQuery) => {
	if (!text || !searchQuery) return text

	const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
	const parts = text.split(regex)

	return parts.map((part, index) => {
		if (regex.test(part)) {
			return (
				<span
					key={index}
					style={{
						backgroundColor: "#fff5af",
						fontWeight: "600",
					}}
				>
					{part}
				</span>
			)
		}
		return part
	})
}
